import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { k8sResourceService } from '@/services/k8sResourceService'
import { HttpError } from '@/lib/httpError'

interface ImageConfigEditorProps {
  flow: any
  canEdit: boolean
  onSave: (updatedManifest: any, configName: string) => void
}

export default function ImageConfigEditor({ flow, canEdit, onSave }: ImageConfigEditorProps) {
  const { t } = useTranslation(['pipeline-studio', 'common'])
  const [isEditing, setIsEditing] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Edit state
  const [editedImageUrl, setEditedImageUrl] = useState('')
  const [editedImageTag, setEditedImageTag] = useState('')
  const [editedPullPolicy, setEditedPullPolicy] = useState('')
  const [selectedPullSecrets, setSelectedPullSecrets] = useState<string[]>([])

  // Available secrets in namespace
  const [availableSecrets, setAvailableSecrets] = useState<string[]>([])
  const [loadingSecrets, setLoadingSecrets] = useState(false)
  const [showSecretDropdown, setShowSecretDropdown] = useState(false)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSecretDropdown(false)
      }
    }

    if (showSecretDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showSecretDropdown])

  const handleEdit = async () => {
    // Initialize edit state with current values
    setEditedImageUrl(flow.spec?.image?.url || '')
    setEditedImageTag(flow.spec?.image?.tag || '')
    setEditedPullPolicy(flow.spec?.imagePullPolicy || '')
    setSelectedPullSecrets(
      flow.spec?.imagePullSecrets && Array.isArray(flow.spec.imagePullSecrets)
        ? flow.spec.imagePullSecrets
        : []
    )
    setIsEditing(true)

    // Fetch available secrets from namespace
    await fetchSecrets()
  }

  const fetchSecrets = async () => {
    if (!flow.metadata?.namespace) return

    setLoadingSecrets(true)
    try {
      const response = await k8sResourceService.getSecrets(flow.metadata.namespace)
      const secretNames = response.items
        .map((secret: any) => secret.metadata?.name)
        .filter((name: string) => name)
      setAvailableSecrets(secretNames)
    } catch (err) {
      console.error('Failed to fetch secrets:', err)
      setAvailableSecrets([])
    } finally {
      setLoadingSecrets(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setShowSecretDropdown(false)
  }

  const toggleSecretSelection = (secretName: string) => {
    setSelectedPullSecrets(prev => {
      if (prev.includes(secretName)) {
        return prev.filter(s => s !== secretName)
      } else {
        return [...prev, secretName]
      }
    })
  }

  const removeSecret = (secretName: string) => {
    setSelectedPullSecrets(prev => prev.filter(s => s !== secretName))
  }

  const handleSave = () => {
    // Create updated flow object
    const updatedFlow = {
      ...flow,
      spec: {
        ...flow.spec,
        image: {
          ...(flow.spec?.image || {}),
          url: editedImageUrl.trim() || undefined,
          tag: editedImageTag.trim() || undefined,
        },
        imagePullPolicy: editedPullPolicy.trim() || undefined,
        imagePullSecrets: selectedPullSecrets.length > 0 ? selectedPullSecrets : undefined,
      }
    }

    // Remove undefined fields
    if (!updatedFlow.spec.image.url && !updatedFlow.spec.image.tag) {
      delete updatedFlow.spec.image
    }
    if (!updatedFlow.spec.imagePullPolicy) {
      delete updatedFlow.spec.imagePullPolicy
    }
    if (!updatedFlow.spec.imagePullSecrets || updatedFlow.spec.imagePullSecrets.length === 0) {
      delete updatedFlow.spec.imagePullSecrets
    }

    // Call parent's save handler (which will open the confirmation modal)
    onSave(updatedFlow, t('detail.imageConfig'))
    setIsEditing(false)
    setShowSecretDropdown(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-text-dark">{t('detail.imageConfig', 'Image Configuration')}</h2>
        {canEdit && !isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('detail.edit', 'Edit')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Image URL */}
          <div>
            <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700 mb-1">
              {t('detail.imageURL', 'Image URL')}
            </label>
            <input
              id="imageUrl"
              type="text"
              value={editedImageUrl}
              onChange={(e) => setEditedImageUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              placeholder="e.g., nodered/node-red"
            />
          </div>

          {/* Image Tag */}
          <div>
            <label htmlFor="imageTag" className="block text-sm font-medium text-gray-700 mb-1">
              {t('detail.imageTag', 'Tag')}
            </label>
            <input
              id="imageTag"
              type="text"
              value={editedImageTag}
              onChange={(e) => setEditedImageTag(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              placeholder="e.g., latest"
            />
          </div>

          {/* Pull Policy */}
          <div>
            <label htmlFor="pullPolicy" className="block text-sm font-medium text-gray-700 mb-1">
              {t('detail.imagePullPolicy', 'Pull Policy')}
            </label>
            <select
              id="pullPolicy"
              value={editedPullPolicy}
              onChange={(e) => setEditedPullPolicy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            >
              <option value="">-- Select --</option>
              <option value="Always">Always</option>
              <option value="IfNotPresent">IfNotPresent</option>
              <option value="Never">Never</option>
            </select>
          </div>

          {/* Pull Secrets */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('detail.imagePullSecrets', 'Pull Secrets')}
            </label>

            {/* Selected Secrets */}
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedPullSecrets.map(secret => (
                <span
                  key={secret}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-800 rounded text-xs font-medium"
                >
                  {secret}
                  <button
                    type="button"
                    onClick={() => removeSecret(secret)}
                    className="hover:text-primary-900"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>

            {/* Dropdown to add secrets */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setShowSecretDropdown(!showSecretDropdown)}
                disabled={loadingSecrets}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm text-left flex items-center justify-between hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-gray-500">
                  {loadingSecrets ? t('common:loading', 'Loading...') : t('detail.selectSecrets', 'Select secrets...')}
                </span>
                <svg
                  className={`w-4 h-4 transition-transform ${showSecretDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown List */}
              {showSecretDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {availableSecrets.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 text-center">
                      {t('detail.noSecretsAvailable', 'No secrets available in this namespace')}
                    </div>
                  ) : (
                    availableSecrets.map(secret => (
                      <label
                        key={secret}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPullSecrets.includes(secret)}
                          onChange={() => toggleSecretSelection(secret)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-900">{secret}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">{t('detail.selectSecretsHelp', 'Select image pull secrets from the list')}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('detail.save', 'Save')}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.imageURL', 'Image URL')}</dt>
            <dd className="mt-1 text-sm text-gray-900 font-mono">{flow.spec?.image?.url || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.imageTag', 'Tag')}</dt>
            <dd className="mt-1 text-sm text-gray-900 font-mono">{flow.spec?.image?.tag || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.imagePullPolicy', 'Pull Policy')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{flow.spec?.imagePullPolicy || '-'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.imagePullSecrets', 'Pull Secrets')}</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {flow.spec?.imagePullSecrets && Array.isArray(flow.spec.imagePullSecrets)
                ? flow.spec.imagePullSecrets.join(', ')
                : '-'}
            </dd>
          </div>
        </dl>
      )}
    </div>
  )
}
